import { type ClientRole, parseRealtimeEvent, type RealtimeEvent } from '@momonos/shared'

/**
 * The realtime client — FR-RT-4.
 *
 * The contract that makes a dropped connection harmless: the socket is a
 * *notification* channel, never a data channel. On every (re)connect the client
 * pulls a fresh snapshot over HTTP first, then applies live events on top. A
 * screen that was offline for ten minutes therefore recovers completely, and no
 * order can be lost because a WebSocket blinked.
 */

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'offline'

export interface RealtimeOptions {
  url: string
  branchId: string
  role: ClientRole
  /** Customer screens narrow to a single order (FR-QR-3). */
  orderId?: string
  getAccessToken?: () => string | null

  /** Pull authoritative state. Called before events resume, every time. */
  onSnapshot: () => Promise<void>
  onEvent: (event: RealtimeEvent) => void
  onStateChange?: (state: ConnectionState) => void

  /** Injected in tests; defaults to the platform globals. */
  socketFactory?: (url: string) => WebSocketLike
  now?: () => number
  setTimeoutImpl?: (handler: () => void, ms: number) => number
  clearTimeoutImpl?: (handle: number) => void
}

/** The slice of WebSocket this client uses, so tests can supply a fake. */
export interface WebSocketLike {
  send(data: string): void
  close(code?: number, reason?: string): void
  addEventListener(
    type: 'open' | 'message' | 'close' | 'error',
    listener: (event: never) => void,
  ): void
  readyState?: number
}

const HEARTBEAT_INTERVAL_MS = 25_000
const MAX_BACKOFF_MS = 30_000
const BASE_BACKOFF_MS = 500

export class RealtimeClient {
  private socket: WebSocketLike | null = null
  private state: ConnectionState = 'offline'
  private attempt = 0
  private heartbeat: number | null = null
  private reconnectTimer: number | null = null
  private stopped = false

  constructor(private readonly options: RealtimeOptions) {}

  get connectionState(): ConnectionState {
    return this.state
  }

  /** Backoff for a given attempt, exposed so the UI can show "retrying in Ns". */
  backoffFor(attempt: number): number {
    const exponential = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS)
    // Jitter stops a room full of screens reconnecting in lockstep after an
    // outage and hammering the worker at the same instant.
    return Math.round(exponential * (0.5 + Math.random() * 0.5))
  }

  async connect(): Promise<void> {
    this.stopped = false
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting')

    // Snapshot first: by the time events start arriving, the screen is current.
    try {
      await this.options.onSnapshot()
    } catch {
      // A failed snapshot is retried by the backoff loop below rather than
      // leaving the screen connected to a stream it cannot interpret.
      this.scheduleReconnect()
      return
    }

    const url = new URL(this.options.url)
    url.searchParams.set('role', this.options.role)
    if (this.options.orderId) url.searchParams.set('orderId', this.options.orderId)

    const token = this.options.getAccessToken?.()
    if (token) url.searchParams.set('token', token)

    const socket = (this.options.socketFactory ?? defaultSocketFactory)(url.toString())
    this.socket = socket

    socket.addEventListener('open', (() => {
      this.attempt = 0
      this.setState('connected')
      this.startHeartbeat()
    }) as never)

    socket.addEventListener('message', ((event: { data: string }) => {
      this.handleMessage(event.data)
    }) as never)

    socket.addEventListener('close', (() => {
      this.stopHeartbeat()
      if (!this.stopped) this.scheduleReconnect()
    }) as never)

    socket.addEventListener('error', (() => {
      // `close` always follows, and that is where reconnect is scheduled.
    }) as never)
  }

  private handleMessage(raw: string): void {
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      return
    }

    const control = parsed as { type?: string }
    if (control.type === 'pong' || control.type === 'hello') return

    // Unknown or future event types are ignored, never fatal: a new event can
    // ship to the server before every kiosk has reloaded (FR-RT-2).
    const event = parseRealtimeEvent(parsed)
    if (event) this.options.onEvent(event)
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    const schedule =
      this.options.setTimeoutImpl ?? ((handler, ms) => setTimeout(handler, ms) as unknown as number)

    const beat = () => {
      try {
        this.socket?.send(JSON.stringify({ type: 'ping' }))
      } catch {
        // The close handler will take it from here.
      }
      this.heartbeat = schedule(beat, HEARTBEAT_INTERVAL_MS)
    }

    this.heartbeat = schedule(beat, HEARTBEAT_INTERVAL_MS)
  }

  private stopHeartbeat(): void {
    if (this.heartbeat == null) return
    const cancel = this.options.clearTimeoutImpl ?? ((handle: number) => clearTimeout(handle))
    cancel(this.heartbeat)
    this.heartbeat = null
  }

  private scheduleReconnect(): void {
    if (this.stopped) return

    this.setState('reconnecting')
    const delay = this.backoffFor(this.attempt)
    this.attempt++

    const schedule =
      this.options.setTimeoutImpl ?? ((handler, ms) => setTimeout(handler, ms) as unknown as number)
    this.reconnectTimer = schedule(() => {
      void this.connect()
    }, delay)
  }

  private setState(next: ConnectionState): void {
    if (this.state === next) return
    this.state = next
    this.options.onStateChange?.(next)
  }

  /** Deliberate shutdown: no reconnect, no heartbeat. */
  disconnect(): void {
    this.stopped = true
    this.stopHeartbeat()

    if (this.reconnectTimer != null) {
      const cancel = this.options.clearTimeoutImpl ?? ((handle: number) => clearTimeout(handle))
      cancel(this.reconnectTimer)
      this.reconnectTimer = null
    }

    try {
      this.socket?.close(1000, 'client disconnect')
    } catch {
      // Already gone.
    }

    this.socket = null
    this.setState('offline')
  }
}

function defaultSocketFactory(url: string): WebSocketLike {
  return new WebSocket(url) as unknown as WebSocketLike
}
